'use client';

import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import { useTranslation } from 'next-i18next/pages';

import { IndustrialCaseSummaryCard } from '@/components/apps/pages/solution/industrial/IndustrialCaseSummaryCard';
import { trackIndustrialCaseSelection } from '@/components/apps/pages/solution/industrial/industrialAnalytics';

type Props = {
  cases: ReadonlyArray<IndustrialCaseSummary>;
};

/**
 * ### IndustrialCaseListContent
 *
 * Renders resolved customer cases after the server-side board request completes.
 * The surrounding page frame owns navigation and remains available while this
 * region is streamed.
 *
 * @param props Published cases for the active solution
 * @returns The resolved case cards or their empty state
 */
export function IndustrialCaseListContent({ cases }: Props) {
  const { t } = useTranslation('common');

  if (cases.length === 0) {
    return (
      <div className="mt-12 border border-[#e2e2de] px-6 py-20 text-center">
        <p className="text-lg font-semibold">
          {t('아직 공개된 고객 사례가 없어요')}
        </p>
        <p className="mt-3 text-[15px] font-medium leading-[1.65] text-[#4f4f4b]">
          {t('준비되는 대로 이곳에 올려둘게요')}
        </p>
      </div>
    );
  }

  return (
    <ul className="mt-12 grid list-none gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cases.map((caseSummary) => (
        <li className="min-w-0" key={caseSummary.slug}>
          <IndustrialCaseSummaryCard
            caseSummary={caseSummary}
            readLabel={t('사례 읽기')}
            metricLabel={t('핵심 지표')}
            onSelect={() =>
              trackIndustrialCaseSelection({
                contentType: 'case_card',
                itemId: caseSummary.slug,
                caseIdentity: {
                  slug: caseSummary.slug,
                  solution: caseSummary.solution,
                  industry: caseSummary.industry,
                },
              })
            }
          />
        </li>
      ))}
    </ul>
  );
}
